import type { ElementKey } from '~/types/game'

/** Flavor names keyed by the mission's dominant required element. */
export const MISSION_NAMES: Record<ElementKey, string[]> = {
  fire: [
    'Ember Imps', 'Cinder Hounds', 'Flare Wisps', 'Ash Crawlers', 'Blaze Beetles',
    'Scorch Rats', 'Kiln Spirits', 'Wildfire Swarm',
  ],
  // Singular since v2.14 (per Javi — the paintings are single creatures;
  // only the fire set was painted as groups and keeps plural names).
  // Collectives (Drowned Roots, Creeping Canopy, Charged Vortex,
  // Rumbling Cairn) already read as one entity and stay unchanged.
  // Puddle Slimes stays plural — Javi's painting shows two of them.
  water: [
    'Puddle Slimes', 'Dew Drake', 'Mist Crab', 'Tide Sprite', 'Brine Eel',
    'Rain Wraith', 'Foam Serpent', 'Drowned Roots',
  ],
  grass: [
    'Overgrown Slime', 'Bramble Beetle', 'Moss Golem', 'Thorn Hare', 'Vine Creeper',
    'Spore Puff', 'Nettle Imp', 'Creeping Canopy',
  ],
  lightning: [
    'Static Sprite', 'Volt Mite', 'Storm Hawk', 'Spark Jackal', 'Arc Wisp',
    'Thunder Grub', 'Flash Fiend', 'Charged Vortex',
  ],
  earth: [
    'Pebble Gnome', 'Mud Golem', 'Boulder Beetle', 'Dust Digger', 'Clay Shambler',
    'Gravel Wolf', 'Sandstone Serpent', 'Rumbling Cairn',
  ],
}

/**
 * 👑 ELITE CHAMPIONS (v2.68, per Javi: "their art is amazing, I would use
 * them for Elite nodes").
 *
 * These eight were the boss flavour names before v0.51 gave bosses real
 * identities (The Sandman, Umbrax, Myriad…). `applyBossSkill` overwrites
 * the rolled name with the skill's boss, so every one of them has been
 * rolled and discarded on every boss node since — unreachable, with eight
 * painted enemies (still + gloat + collapse) orphaned alongside them.
 *
 * They now headline the HARD-tier missions on elite nodes, keyed to the
 * mission's dominant element so a lava beast never fronts a water fight.
 * Nothing about the fight changes — same tier, same requirements, same
 * bands — the toughest mission on an elite node simply has a name and a
 * face now.
 */
export const ELITE_CHAMPIONS: Record<ElementKey, string[]> = {
  fire: ['Magma Behemoth'],
  water: ['Tempest Leviathan', 'The Sunken King', 'Avalanche Alpha'],
  grass: ['The Verdant Maw'],
  lightning: ['Stormcrown Wyrm'],
  earth: ['The Ruin Colossus', 'The Hollow Titan'],
}

/** Kept for save compatibility: boss missions rolled before v0.51 still
 * carry one of these names, and their art is the champion art above. */
export const BOSS_NAMES = Object.values(ELITE_CHAMPIONS).flat()

/** 👑 v5.12 (per Javi): champions are a CHASE — their codex entry is
 * earned by FIGHTING and COMPLETING the mission that wears one, never
 * by merely seeing it spawn. This set gates the recording sites. */
export const CHAMPION_SLUGS = new Set(
  BOSS_NAMES.map(n => n.toLowerCase().replace(/[^a-z0-9]+/g, '-')),
)
